/**
 * CSV parse + payload helpers for roster bulk student import (POST /v1/roster/students/bulk).
 * Does not include display_name, parent_user_ids, or invite/enrollment fields.
 */
import type { RosterBulkCreateStudentItem } from '../../modules/roster/api/roster.client';
import { validateEmail } from '../../utils/validation';

/** Canonical CSV headers aligned with RosterBulkCreateStudentItem. */
export const ROSTER_BULK_STUDENT_CANONICAL_HEADERS = [
  'first_name',
  'last_name',
  'email',
  'phone',
  'admission_no',
  'gender',
  'dob',
] as const;

const DOB_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Map alternate header names to canonical keys after lowercasing.
 * `date_of_birth` / `admission_number` match common spreadsheet exports.
 */
const HEADER_ALIASES: Record<string, string> = {
  date_of_birth: 'dob',
  admission_number: 'admission_no',
};

export interface ParsedStudentCsvRow {
  /** 1-based line index in file (data rows) for display. */
  sourceRowIndex: number;
  item: RosterBulkCreateStudentItem;
}

export interface ParseBulkStudentsCsvResult {
  rows: ParsedStudentCsvRow[];
  warnings: string[];
  errors: string[];
}

export function parseCsvRaw(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let i = 0;
  let inQuotes = false;

  const pushField = () => {
    row.push(field);
    field = '';
  };

  const pushRow = () => {
    rows.push(row);
    row = [];
  };

  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (c === ',') {
      pushField();
      i++;
      continue;
    }
    if (c === '\r') {
      i++;
      continue;
    }
    if (c === '\n') {
      pushField();
      pushRow();
      i++;
      continue;
    }
    field += c;
    i++;
  }
  pushField();
  if (row.length > 1 || (row.length === 1 && row[0].trim() !== '')) {
    pushRow();
  }
  return rows;
}

function normalizeHeader(h: string): string {
  const t = h.trim().toLowerCase();
  return HEADER_ALIASES[t] ?? t;
}

function isRowBlank(cells: string[]): boolean {
  return cells.every((c) => !String(c).trim());
}

const CANONICAL = new Set<string>(ROSTER_BULK_STUDENT_CANONICAL_HEADERS);

export function parseBulkStudentsCsv(text: string): ParseBulkStudentsCsvResult {
  const warnings: string[] = [];
  const errors: string[] = [];
  const grid = parseCsvRaw(text.replace(/^\uFEFF/, ''));
  if (grid.length === 0) {
    errors.push('The file is empty.');
    return { rows: [], warnings, errors };
  }

  const headerCells = grid[0].map((h) => normalizeHeader(h));
  const indexByCanon: Record<string, number> = {};
  const unknownHeaders: string[] = [];

  headerCells.forEach((h, idx) => {
    if (!h) return;
    if (CANONICAL.has(h)) {
      if (indexByCanon[h] === undefined) indexByCanon[h] = idx;
    } else {
      unknownHeaders.push(h);
    }
  });

  if (unknownHeaders.length) {
    warnings.push(`Ignored column(s) not used by roster bulk import: ${[...new Set(unknownHeaders)].join(', ')}.`);
  }

  if (indexByCanon.first_name === undefined) {
    errors.push('Missing required column: first_name.');
  }

  if (errors.length) {
    return { rows: [], warnings, errors };
  }

  const rows: ParsedStudentCsvRow[] = [];

  for (let r = 1; r < grid.length; r++) {
    const line = grid[r];
    if (isRowBlank(line)) continue;

    const firstName = (line[indexByCanon.first_name] ?? '').trim();
    if (!firstName) {
      errors.push(`Row ${r + 1}: first_name is required (or remove blank rows).`);
      continue;
    }

    const lastName =
      indexByCanon.last_name !== undefined ? (line[indexByCanon.last_name] ?? '').trim() : '';
    const emailRaw = indexByCanon.email !== undefined ? (line[indexByCanon.email] ?? '').trim() : '';
    const phoneRaw = indexByCanon.phone !== undefined ? (line[indexByCanon.phone] ?? '').trim() : '';
    const admissionNo =
      indexByCanon.admission_no !== undefined ? (line[indexByCanon.admission_no] ?? '').trim() : '';
    const genderRaw = indexByCanon.gender !== undefined ? (line[indexByCanon.gender] ?? '').trim() : '';
    const dobRaw = indexByCanon.dob !== undefined ? (line[indexByCanon.dob] ?? '').trim() : '';

    if (emailRaw && !validateEmail(emailRaw)) {
      errors.push(`Row ${r + 1}: email format is invalid.`);
      continue;
    }

    if (dobRaw && !DOB_PATTERN.test(dobRaw)) {
      errors.push(`Row ${r + 1}: dob must be YYYY-MM-DD when provided.`);
      continue;
    }

    const item: RosterBulkCreateStudentItem = { first_name: firstName };
    if (lastName) item.last_name = lastName;
    if (emailRaw) item.email = emailRaw;
    if (phoneRaw) item.phone = phoneRaw;
    if (admissionNo) item.admission_no = admissionNo;
    if (genderRaw) item.gender = genderRaw;
    if (dobRaw) item.dob = dobRaw;

    rows.push({ sourceRowIndex: r + 1, item });
  }

  if (rows.length === 0 && errors.length === 0) {
    errors.push('No data rows found after the header.');
  }

  return { rows, warnings, errors };
}

export function buildRosterBulkStudentsPayloadFromParsed(
  rows: ParsedStudentCsvRow[],
  skip_duplicates: boolean,
  continue_on_error: boolean
) {
  return {
    students: rows.map((r) => r.item),
    skip_duplicates,
    continue_on_error,
  };
}
